/**
 * Audit Trail Service
 * Centralized service for logging all admin activities across the application
 */

import { getAPIBaseURL } from '../api/apiService';

// Local JWT auth — this app does NOT use Cognito. Token is stored in localStorage
// by AuthContext after login (key: 'access_token').
const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Log an audit trail entry
 * @param {string} action - Action type: CREATE, UPDATE, DELETE, GENERATE, etc.
 * @param {string} resourceType - Type of resource: employee, establishment, form, payroll, etc.
 * @param {string} resourceId - ID of the resource (optional)
 * @param {string} resourceName - Human-readable name (optional)
 * @param {string} description - Detailed description of the action
 * @param {object} metadata - Additional context (optional)
 * @returns {Promise<object>} Response from the API
 */
export const logAudit = async (action, resourceType, resourceId, resourceName, description, metadata = null) => {
  try {
    const API_BASE_URL = getAPIBaseURL();

    const response = await fetch(`${API_BASE_URL}/api/audit/log`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({
        action,
        resource_type: resourceType,
        resource_id: resourceId,
        resource_name: resourceName,
        description,
        metadata
      })
    });

    if (!response.ok) {
      console.error('Failed to log audit trail:', await response.text());
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error('Error logging audit trail:', error);
    return null;
  }
};

/**
 * Get audit logs with optional filters
 */
export const getAuditLogs = async (filters = {}) => {
  try {
    const API_BASE_URL = getAPIBaseURL();

    const params = new URLSearchParams();
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);
    if (filters.action) params.append('action', filters.action);
    if (filters.resource_type) params.append('resource_type', filters.resource_type);
    if (filters.username) params.append('username', filters.username);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);

    const response = await fetch(`${API_BASE_URL}/api/audit/logs?${params}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch audit logs');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching audit logs:', error);
    throw error;
  }
};

/**
 * Get recent audit logs
 */
export const getRecentAuditLogs = async (limit = 50) => {
  try {
    const API_BASE_URL = getAPIBaseURL();

    const response = await fetch(`${API_BASE_URL}/api/audit/recent?limit=${limit}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch recent audit logs');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching recent audit logs:', error);
    throw error;
  }
};

/**
 * Get audit statistics
 */
export const getAuditStats = async (days = 7) => {
  try {
    const API_BASE_URL = getAPIBaseURL();

    const response = await fetch(`${API_BASE_URL}/api/audit/stats?days=${days}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch audit stats');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching audit stats:', error);
    throw error;
  }
};

// Helper functions for common audit actions

export const auditActions = {
  // Employee actions
  createEmployee: (employeeId, employeeName, metadata) =>
    logAudit('CREATE', 'employee', employeeId, employeeName, `Created new employee: ${employeeName}`, metadata),

  updateEmployee: (employeeId, employeeName, changes) =>
    logAudit('UPDATE', 'employee', employeeId, employeeName, `Updated employee: ${employeeName}`, { changes }),

  deleteEmployee: (employeeId, employeeName) =>
    logAudit('DELETE', 'employee', employeeId, employeeName, `Deleted employee: ${employeeName}`),

  // Organization actions
  createOrganization: (estId, estName, metadata) =>
    logAudit('CREATE', 'organization', estId, estName, `Created new establishment: ${estName}`, metadata),

  updateOrganization: (estId, estName, changes) =>
    logAudit('UPDATE', 'organization', estId, estName, `Updated establishment: ${estName}`, { changes }),

  deleteOrganization: (estId, estName) =>
    logAudit('DELETE', 'organization', estId, estName, `Deleted establishment: ${estName}`),

  // Form generation actions
  generateForm: (formType, formName, targetName, metadata) =>
    logAudit('GENERATE', 'form', formType, formName, `Generated ${formName} for ${targetName}`, metadata),

  // Payroll actions
  processPayroll: (month, year, estName, metadata) =>
    logAudit('PROCESS', 'payroll', `${month}-${year}`, `Payroll ${month}/${year}`, `Processed payroll for ${estName} - ${month}/${year}`, metadata),

  approvePayroll: (month, year, estName) =>
    logAudit('APPROVE', 'payroll', `${month}-${year}`, `Payroll ${month}/${year}`, `Approved payroll for ${estName} - ${month}/${year}`),

  // Leave actions
  createLeave: (employeeName, leaveType, metadata) =>
    logAudit('CREATE', 'leave', null, employeeName, `Created leave request for ${employeeName} - ${leaveType}`, metadata),

  approveLeave: (employeeName, leaveType, metadata) =>
    logAudit('APPROVE', 'leave', null, employeeName, `Approved leave for ${employeeName} - ${leaveType}`, metadata),

  rejectLeave: (employeeName, leaveType, reason) =>
    logAudit('REJECT', 'leave', null, employeeName, `Rejected leave for ${employeeName} - ${leaveType}`, { reason }),

  // Attendance actions
  markAttendance: (date, metadata) =>
    logAudit('UPDATE', 'attendance', null, date, `Updated attendance for ${date}`, metadata),

  // Bonus/Allowance actions
  addBonus: (employeeName, amount, type) =>
    logAudit('CREATE', 'bonus', null, employeeName, `Added ${type} of ₹${amount} for ${employeeName}`, { amount, type }),

  // Asset actions
  createAsset: (assetId, assetName, metadata) =>
    logAudit('CREATE', 'asset', assetId, assetName, `Created new asset: ${assetName}`, metadata),

  assignAsset: (assetName, employeeName) =>
    logAudit('ASSIGN', 'asset', null, assetName, `Assigned ${assetName} to ${employeeName}`, { employee: employeeName }),

  // Invoice actions
  createInvoice: (invoiceId, clientName, amount) =>
    logAudit('CREATE', 'invoice', invoiceId, `Invoice ${invoiceId}`, `Created invoice for ${clientName} - ₹${amount}`, { amount }),

  // Document actions
  uploadDocument: (docName, category) =>
    logAudit('UPLOAD', 'document', null, docName, `Uploaded document: ${docName} (${category})`, { category }),
};

export default {
  logAudit,
  getAuditLogs,
  getRecentAuditLogs,
  getAuditStats,
  auditActions
};